// FinTrack — Zaključni list (Trial Balance / Probni bilans)
// Početno stanje + promet za period = ukupno + saldo

import { state } from './state.js';
import { sb } from './supabase.js';
import { toast, showLoading, hideLoading } from './ui.js';
import { fNum, pDate } from './utils.js';
import { bindDatePicker } from './datepicker.js';
import XLSX from 'xlsx-js-style';

let zlData = []; // merged trial balance rows

export function initZakljucniList() {
  const now = new Date();
  const y = now.getFullYear();
  const fromEl = document.getElementById('zlFrom');
  const toEl = document.getElementById('zlTo');
  if (fromEl) { fromEl.value = '01.01.' + y; bindDatePicker(fromEl); }
  const lastDay = new Date(y, now.getMonth() + 1, 0).getDate();
  if (toEl) { toEl.value = String(lastDay).padStart(2, '0') + '.' + String(now.getMonth() + 1).padStart(2, '0') + '.' + y; bindDatePicker(toEl); }
}

export async function generateZl() {
  const fromStr = document.getElementById('zlFrom')?.value;
  const toStr = document.getElementById('zlTo')?.value;
  const fromDate = pDate(fromStr);
  const toDate = pDate(toStr);
  if (!fromDate || !toDate) { toast('Unesite ispravne datume (DD.MM.GGGG)', 'err'); return; }
  if (fromDate > toDate) { toast('Datum "Od" mora biti pre "Do"', 'err'); return; }

  showLoading();
  try {
    // Parallel fetch: pocetno stanje + knjizenja entries
    const [psRes, kjRes] = await Promise.all([
      sb.from('pocetno_stanje').select('konto,naziv_konta,duguje,potrazuje,tip').order('konto'),
      sb.from('knjizenja_entries').select('rs_code,rs_name,detail_level,debit,credit,analytic_code,analytic_name,booking_date')
        .eq('user_id', state.CU.id).eq('is_final', true)
    ]);
    if (psRes.error) throw psRes.error;
    if (kjRes.error) throw kjRes.error;

    const psRows = (psRes.data || []).filter(p => p.tip !== 'X');
    const allKj = kjRes.data || [];

    // Filter knjizenja by date range
    const kjRows = allKj.filter(e => {
      const d = pDate(e.booking_date);
      return d && d >= fromDate && d <= toDate;
    });

    // Build promet map: konto → { d, p }
    const prometMap = new Map();
    kjRows.forEach(e => {
      if (e.detail_level === 'ANALYTIC') return; // analytics are included in PARENT/SUMMARY
      const k = e.rs_code;
      if (!prometMap.has(k)) prometMap.set(k, { d: 0, p: 0, name: e.rs_name || '' });
      const m = prometMap.get(k);
      m.d += parseFloat(e.debit) || 0;
      m.p += parseFloat(e.credit) || 0;
      if (!m.name && e.rs_name) m.name = e.rs_name;
    });

    // Build PS map: konto → { d, p, name, tip }
    const psMap = new Map();
    psRows.forEach(p => {
      if (p.tip === 'A' || p.tip === 'K') return; // skip analytics/komitent rows
      psMap.set(p.konto.trim(), {
        d: parseFloat(p.duguje) || 0,
        p: parseFloat(p.potrazuje) || 0,
        name: p.naziv_konta || '',
        tip: p.tip
      });
    });

    // Union of all kontos
    const allKontos = new Set([...psMap.keys(), ...prometMap.keys()]);

    // Build merged rows
    zlData = [];
    let redBr = 0;
    // Hijerarhijsko sortiranje: sub-konto (0118) ide odmah iza parenta (011)
    const allList = [...allKontos];
    // Sort key: za svaki konto nađi parent prefix i napravi ključ parent+suffix
    // Npr. 0118 → parent je 011, key = "011|0118"
    //      011  → nema parenta, key = "011|"
    //      1012 → parent je 101, key = "101|1012"
    const sortKey = (k) => {
      // Traži najduži parent prefix koji POSTOJI kao zaseban konto
      for (let len = k.length - 1; len >= 1; len--) {
        const prefix = k.substring(0, len);
        if (prefix !== k && allKontos.has(prefix)) {
          return prefix + '|' + k;
        }
      }
      return k + '|';
    };
    const sorted = allList.sort((a, b) => {
      const ka = sortKey(a), kb = sortKey(b);
      return ka.localeCompare(kb, undefined, { numeric: true });
    });

    for (const k of sorted) {
      const ps = psMap.get(k) || { d: 0, p: 0, name: '', tip: '' };
      const pr = prometMap.get(k) || { d: 0, p: 0, name: '' };
      const ukD = ps.d + pr.d;
      const ukP = ps.p + pr.p;
      const saldo = ukD - ukP;
      // Skip rows where everything is zero
      if (ukD === 0 && ukP === 0 && ps.d === 0 && ps.p === 0) continue;

      redBr++;
      zlData.push({
        redBr,
        konto: k,
        naziv: ps.name || pr.name || k,
        psD: ps.d, psP: ps.p,
        prD: pr.d, prP: pr.p,
        ukD, ukP, saldo
      });
    }

    renderZl();
    document.getElementById('zlExpBtn').style.display = zlData.length ? '' : 'none';
    document.getElementById('zlEmpty').style.display = 'none';
  } catch (e) {
    toast('Greška: ' + (e.message || e), 'err');
  }
  hideLoading();
}

export function filterZl() { renderZl(); }

function renderZl() {
  const out = document.getElementById('zlOutput');
  if (!out) return;
  out.textContent = '';
  if (!zlData.length) { document.getElementById('zlEmpty').style.display = ''; return; }

  const q = (document.getElementById('zlSearch')?.value || '').toLowerCase();
  const filtered = q ? zlData.filter(r => r.konto.toLowerCase().includes(q) || r.naziv.toLowerCase().includes(q)) : zlData;

  // Summary
  const totD = filtered.reduce((s, r) => s + r.ukD, 0);
  const totC = filtered.reduce((s, r) => s + r.ukP, 0);
  const count = document.getElementById('zlCount');
  if (count) count.textContent = filtered.length + ' konta | Ukupno D: ' + fNum(totD) + ' | P: ' + fNum(totC) + ' | Saldo: ' + fNum(totD - totC);

  const tbl = document.createElement('table');
  tbl.className = 'dtbl';
  tbl.style.fontSize = '12px';

  // Header
  const thead = document.createElement('thead');
  const hr1 = document.createElement('tr');
  ['R.br', 'Konto', 'Naziv konta', 'PS Duguje', 'PS Potražuje', 'Promet D', 'Promet P', 'Ukupno D', 'Ukupno P', 'Saldo'].forEach((h, i) => {
    const th = document.createElement('th');
    th.textContent = h;
    if (i >= 3) th.style.textAlign = 'right';
    if (i === 2) th.style.minWidth = '200px';
    th.style.fontSize = '11px';
    hr1.appendChild(th);
  });
  thead.appendChild(hr1);
  tbl.appendChild(thead);

  // Body
  const tbody = document.createElement('tbody');
  const mn = 'var(--mn)';
  filtered.forEach(r => {
    const tr = document.createElement('tr');

    const td0 = document.createElement('td'); td0.textContent = r.redBr; td0.style.cssText = 'text-align:center;color:var(--tx3);font-size:10px';
    const td1 = document.createElement('td'); td1.textContent = r.konto; td1.style.cssText = 'font-family:' + mn + ';font-weight:600;color:var(--acc)';
    const td2 = document.createElement('td'); td2.textContent = r.naziv; td2.style.cssText = 'max-width:250px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap';

    const numStyle = 'text-align:right;font-family:' + mn + ';font-size:11px';
    const mkNum = (v) => { const td = document.createElement('td'); td.style.cssText = numStyle; td.textContent = v ? fNum(v) : ''; return td; };

    const td9 = document.createElement('td');
    td9.style.cssText = numStyle + ';font-weight:600';
    td9.textContent = fNum(r.saldo);
    td9.style.color = r.saldo > 0 ? 'var(--dng)' : r.saldo < 0 ? 'var(--suc)' : 'var(--tx3)';

    tr.appendChild(td0); tr.appendChild(td1); tr.appendChild(td2);
    tr.appendChild(mkNum(r.psD)); tr.appendChild(mkNum(r.psP));
    tr.appendChild(mkNum(r.prD)); tr.appendChild(mkNum(r.prP));
    tr.appendChild(mkNum(r.ukD)); tr.appendChild(mkNum(r.ukP));
    tr.appendChild(td9);
    tbody.appendChild(tr);
  });

  // Totals row
  const trT = document.createElement('tr');
  trT.style.cssText = 'font-weight:700;background:rgba(232,184,77,.1);border-top:2px solid var(--acc)';
  const tdE1 = document.createElement('td'); tdE1.textContent = '';
  const tdE2 = document.createElement('td'); tdE2.textContent = '';
  const tdLbl = document.createElement('td'); tdLbl.textContent = 'UKUPNO'; tdLbl.style.fontWeight = '700';
  trT.appendChild(tdE1); trT.appendChild(tdE2); trT.appendChild(tdLbl);
  const numStyleB = 'text-align:right;font-family:var(--mn);font-weight:700;font-size:12px';
  const totals = [
    filtered.reduce((s, r) => s + r.psD, 0), filtered.reduce((s, r) => s + r.psP, 0),
    filtered.reduce((s, r) => s + r.prD, 0), filtered.reduce((s, r) => s + r.prP, 0),
    totD, totC, totD - totC
  ];
  totals.forEach((v, i) => {
    const td = document.createElement('td');
    td.style.cssText = numStyleB;
    td.textContent = fNum(v);
    if (i === 6) td.style.color = v > 0 ? 'var(--dng)' : v < 0 ? 'var(--suc)' : '';
    trT.appendChild(td);
  });
  tbody.appendChild(trT);

  tbl.appendChild(tbody);
  const card = document.createElement('div');
  card.className = 'card';
  card.style.cssText = 'padding:0;overflow:auto';
  card.appendChild(tbl);
  out.appendChild(card);
}

// Excel export
const XH = { font: { bold: true, color: { rgb: 'FFFFFF' }, sz: 10 }, fill: { fgColor: { rgb: '0F1923' } }, alignment: { horizontal: 'center' } };
const XHR = { ...XH, alignment: { horizontal: 'right' } };
const XN = { font: { sz: 9 }, numFmt: '#,##0.00' };
const XB = { font: { bold: true, sz: 10 }, numFmt: '#,##0.00', fill: { fgColor: { rgb: 'FEF08A' } } };
function xc(v, s) { return { v: v ?? '', s: s || {}, t: typeof v === 'number' ? 'n' : 's' }; }

export function expZlXlsx() {
  if (!zlData.length) { toast('Nema podataka za export', 'err'); return; }
  const toStr = document.getElementById('zlTo')?.value || '';

  const rows = [];
  // Row 0: Company
  rows.push([xc('Privredno društvo za proizvodnju papira i kartona ECOR SERBIA DOO KRALJEVO', XH), ...Array(9).fill(xc('', XH))]);
  // Row 1: PIB
  rows.push([xc('PIB: 108055255', {}), ...Array(9).fill(xc('', {}))]);
  // Row 2: Header 1
  rows.push([xc('Red.br', XH), xc('Konto', XH), xc('Naziv konta', XH), xc('Početno stanje', XH), xc('', XH), xc('P r o m e t', XH), xc('', XH), xc('U K U P N O', XH), xc('', XH), xc('SALDO', XH)]);
  // Row 3: Header 2
  rows.push([xc('', XH), xc('', XH), xc('', XH), xc('Duguje', XH), xc('Potražuje', XH), xc('Duguje', XH), xc('Potražuje', XH), xc('Duguje', XH), xc('Potražuje', XH), xc('', XH)]);
  // Row 4: Column numbers
  rows.push(Array.from({ length: 10 }, (_, i) => xc(i + 1, { font: { sz: 8, color: { rgb: '999999' } }, alignment: { horizontal: 'center' } })));

  // Data rows
  zlData.forEach(r => {
    rows.push([
      xc(r.redBr, { font: { sz: 9 }, alignment: { horizontal: 'center' } }),
      xc(r.konto, { font: { sz: 9, bold: true } }),
      xc(r.naziv, { font: { sz: 9 } }),
      xc(r.psD || null, XN), xc(r.psP || null, XN),
      xc(r.prD || null, XN), xc(r.prP || null, XN),
      xc(r.ukD || null, XN), xc(r.ukP || null, XN),
      xc(r.saldo, { ...XN, font: { sz: 9, bold: true } })
    ]);
  });

  // Totals row
  const t = zlData.reduce((s, r) => ({ psD: s.psD + r.psD, psP: s.psP + r.psP, prD: s.prD + r.prD, prP: s.prP + r.prP, ukD: s.ukD + r.ukD, ukP: s.ukP + r.ukP }), { psD: 0, psP: 0, prD: 0, prP: 0, ukD: 0, ukP: 0 });
  rows.push([xc('', XB), xc('', XB), xc('UKUPNO', XB), xc(t.psD, XB), xc(t.psP, XB), xc(t.prD, XB), xc(t.prP, XB), xc(t.ukD, XB), xc(t.ukP, XB), xc(t.ukD - t.ukP, XB)]);

  // Build worksheet
  const ws = XLSX.utils.aoa_to_sheet(rows.map(r => r.map(c => c.v)));
  // Apply styles
  rows.forEach((row, ri) => row.forEach((cell, ci) => {
    const addr = XLSX.utils.encode_cell({ r: ri, c: ci });
    if (ws[addr]) ws[addr].s = cell.s;
    if (cell.t === 'n' && ws[addr]) ws[addr].t = 'n';
  }));
  // Merges
  ws['!merges'] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: 9 } },  // Company name
    { s: { r: 2, c: 3 }, e: { r: 2, c: 4 } },    // Početno stanje
    { s: { r: 2, c: 5 }, e: { r: 2, c: 6 } },    // Promet
    { s: { r: 2, c: 7 }, e: { r: 2, c: 8 } },    // UKUPNO
    { s: { r: 2, c: 9 }, e: { r: 3, c: 9 } },    // SALDO (2 rows)
    { s: { r: 2, c: 0 }, e: { r: 3, c: 0 } },    // Red.br
    { s: { r: 2, c: 1 }, e: { r: 3, c: 1 } },    // Konto
    { s: { r: 2, c: 2 }, e: { r: 3, c: 2 } },    // Naziv
  ];
  // Column widths
  ws['!cols'] = [{ wch: 6 }, { wch: 8 }, { wch: 35 }, { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 15 }];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Zaključni list');
  const fileName = 'Zakljucni_list_' + toStr.replace(/\./g, '_') + '.xlsx';
  XLSX.writeFile(wb, fileName, { bookSST: false, cellStyles: true });
  toast('Exportovano: ' + fileName);

  // Audit log
  sb.rpc('log_audit', { p_user_id: state.CU.id, p_action: 'export_zakljucni_list', p_details: { period: document.getElementById('zlFrom')?.value + ' - ' + toStr, rows: zlData.length } }).catch(() => {});
}
